import * as THREE from 'three';
import Stats from 'three/addons/libs/stats.module.js';
import { GUI } from 'three/addons/libs/lil-gui.module.min.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { 
  GodRaysFakeSunShader, 
  GodRaysDepthMaskShader, 
  GodRaysCombineShader, 
  GodRaysGenerateShader 
} from 'three/addons/shaders/GodRaysShader.js';

export class GodRays {
  private container: HTMLElement;
  private camera: THREE.PerspectiveCamera;
  private scene: THREE.Scene;
  public renderer: THREE.WebGLRenderer;
  private controls: OrbitControls;
  private materialDepth: THREE.MeshDepthMaterial;
  private sphereMesh: THREE.Mesh;
  private sunPosition: THREE.Vector3;
  private clipPosition: THREE.Vector4;
  private screenSpacePosition: THREE.Vector3;
  private postprocessing: any = { enabled: true };
  private orbitRadius: number = 200;
  private bgColor: number = 0x000511;
  private sunColor: number = 0xffee00;
  private params = {
    fGodRayIntensity: 0.75,
    sunColor: 0xffee00
  };
  private gui: GUI | null = null;
  private stats: Stats;

  constructor(container: HTMLElement) {
    this.container = container;

    this.camera = new THREE.PerspectiveCamera(70, container.clientWidth / container.clientHeight, 1, 3000);
    this.camera.position.z = 200;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(this.bgColor);

    this.materialDepth = new THREE.MeshDepthMaterial();
    this.materialDepth.depthPacking = THREE.RGBADepthPacking;

    this.sunPosition = new THREE.Vector3(0, 1000, -1000);
    this.clipPosition = new THREE.Vector4();
    this.screenSpacePosition = new THREE.Vector3();

    // Load tree model
    const loader = new OBJLoader();
    loader.load('models/tree.obj', 
      (object: THREE.Group) => {
        object.position.set(0, -150, -150);
        object.scale.multiplyScalar(400);
        this.scene.add(object);
      },
      undefined,
      (error) => {
        console.error('Error loading tree model:', error);
      }
    );

    // Create orbiting sphere
    const geo = new THREE.SphereGeometry(1, 20, 10);
    this.sphereMesh = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: 0x000000 }));
    this.sphereMesh.scale.multiplyScalar(20);
    this.scene.add(this.sphereMesh);

    // Setup renderer
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setClearColor(0xffffff);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.autoClear = false;
    container.appendChild(this.renderer.domElement);

    // Setup controls
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.minDistance = 50;
    this.controls.maxDistance = 500;

    // Setup stats
    this.stats = new Stats();
    container.appendChild(this.stats.dom);

    this.initPostprocessing(container.clientWidth, container.clientHeight);
    this.setupGUI();

    window.addEventListener('resize', () => this.onWindowResize());
    this.renderer.setAnimationLoop(() => this.animate());
  }

  private setupGUI() {
    const gui = new GUI();
    const folder = gui.addFolder('God Rays');
    folder.add(this.params, 'fGodRayIntensity', 0, 2).name('Intensity').onChange((value: number) => {
      if (this.postprocessing.godrayCombineUniforms) {
        this.postprocessing.godrayCombineUniforms.fGodRayIntensity.value = value;
      }
    });
    folder.addColor(this.params, 'sunColor').name('Light Color').onChange((value: number) => {
      if (this.postprocessing.godraysFakeSunUniforms) {
        this.postprocessing.godraysFakeSunUniforms.sunColor.value.setHex(value);
      }
    });
    folder.close();
    this.gui = gui;
  }

  private initPostprocessing(renderTargetWidth: number, renderTargetHeight: number) {
    this.postprocessing.scene = new THREE.Scene();

    this.postprocessing.camera = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, -10000, 10000);
    this.postprocessing.camera.position.z = 100;

    this.postprocessing.scene.add(this.postprocessing.camera);

    this.postprocessing.rtTextureColors = new THREE.WebGLRenderTarget(renderTargetWidth, renderTargetHeight, { type: THREE.HalfFloatType });
    this.postprocessing.rtTextureDepth = new THREE.WebGLRenderTarget(renderTargetWidth, renderTargetHeight, { type: THREE.HalfFloatType });
    this.postprocessing.rtTextureDepthMask = new THREE.WebGLRenderTarget(renderTargetWidth, renderTargetHeight, { type: THREE.HalfFloatType });

    const adjustedWidth = renderTargetWidth * (1.0 / 4.0);
    const adjustedHeight = renderTargetHeight * (1.0 / 4.0);
    this.postprocessing.rtTextureGodRays1 = new THREE.WebGLRenderTarget(adjustedWidth, adjustedHeight, { type: THREE.HalfFloatType });
    this.postprocessing.rtTextureGodRays2 = new THREE.WebGLRenderTarget(adjustedWidth, adjustedHeight, { type: THREE.HalfFloatType });

    const godraysMaskShader = GodRaysDepthMaskShader;
    this.postprocessing.godrayMaskUniforms = THREE.UniformsUtils.clone(godraysMaskShader.uniforms);
    this.postprocessing.materialGodraysDepthMask = new THREE.ShaderMaterial({
      uniforms: this.postprocessing.godrayMaskUniforms,
      vertexShader: godraysMaskShader.vertexShader,
      fragmentShader: godraysMaskShader.fragmentShader
    });

    const godraysGenShader = GodRaysGenerateShader;
    this.postprocessing.godrayGenUniforms = THREE.UniformsUtils.clone(godraysGenShader.uniforms);
    this.postprocessing.materialGodraysGenerate = new THREE.ShaderMaterial({
      uniforms: this.postprocessing.godrayGenUniforms,
      vertexShader: godraysGenShader.vertexShader,
      fragmentShader: godraysGenShader.fragmentShader
    });

    const godraysCombineShader = GodRaysCombineShader;
    this.postprocessing.godrayCombineUniforms = THREE.UniformsUtils.clone(godraysCombineShader.uniforms);
    this.postprocessing.materialGodraysCombine = new THREE.ShaderMaterial({
      uniforms: this.postprocessing.godrayCombineUniforms,
      vertexShader: godraysCombineShader.vertexShader,
      fragmentShader: godraysCombineShader.fragmentShader
    });

    const godraysFakeSunShader = GodRaysFakeSunShader;
    this.postprocessing.godraysFakeSunUniforms = THREE.UniformsUtils.clone(godraysFakeSunShader.uniforms);
    this.postprocessing.materialGodraysFakeSun = new THREE.ShaderMaterial({
      uniforms: this.postprocessing.godraysFakeSunUniforms,
      vertexShader: godraysFakeSunShader.vertexShader,
      fragmentShader: godraysFakeSunShader.fragmentShader
    });

    this.postprocessing.godraysFakeSunUniforms.bgColor.value.setHex(this.bgColor);
    this.postprocessing.godraysFakeSunUniforms.sunColor.value.setHex(this.sunColor);
    this.postprocessing.godrayCombineUniforms.fGodRayIntensity.value = this.params.fGodRayIntensity;

    this.postprocessing.quad = new THREE.Mesh(
      new THREE.PlaneGeometry(1.0, 1.0),
      this.postprocessing.materialGodraysGenerate
    );
    this.postprocessing.quad.position.z = -9900;
    this.postprocessing.scene.add(this.postprocessing.quad);
  }

  private onWindowResize() {
    const renderTargetWidth = this.container.clientWidth;
    const renderTargetHeight = this.container.clientHeight;

    this.camera.aspect = renderTargetWidth / renderTargetHeight;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(renderTargetWidth, renderTargetHeight);
    this.postprocessing.rtTextureColors.setSize(renderTargetWidth, renderTargetHeight);
    this.postprocessing.rtTextureDepth.setSize(renderTargetWidth, renderTargetHeight);
    this.postprocessing.rtTextureDepthMask.setSize(renderTargetWidth, renderTargetHeight);

    const adjustedWidth = renderTargetWidth * (1.0 / 4.0);
    const adjustedHeight = renderTargetHeight * (1.0 / 4.0);
    this.postprocessing.rtTextureGodRays1.setSize(adjustedWidth, adjustedHeight);
    this.postprocessing.rtTextureGodRays2.setSize(adjustedWidth, adjustedHeight);
  }

  private getStepSize(filterLen: number, tapsPerPass: number, pass: number): number {
    return filterLen * Math.pow(tapsPerPass, -pass);
  }

  private filterGodRays(inputTex: THREE.Texture, renderTarget: THREE.WebGLRenderTarget, stepSize: number) {
    this.postprocessing.scene.overrideMaterial = this.postprocessing.materialGodraysGenerate;
    this.postprocessing.godrayGenUniforms['fStepSize'].value = stepSize;
    this.postprocessing.godrayGenUniforms['tInput'].value = inputTex;

    this.renderer.setRenderTarget(renderTarget);
    this.renderer.render(this.postprocessing.scene, this.postprocessing.camera);
    this.postprocessing.scene.overrideMaterial = null;
  }

  public setLightColor(color: number) {
    this.params.sunColor = color;
    if (this.postprocessing.godraysFakeSunUniforms) {
      this.postprocessing.godraysFakeSunUniforms.sunColor.value.setHex(color);
    }
  }

  public setLightIntensity(intensity: number) {
    this.params.fGodRayIntensity = intensity;
    if (this.postprocessing.godrayCombineUniforms) {
      this.postprocessing.godrayCombineUniforms.fGodRayIntensity.value = intensity;
    }
  }

  private animate() {
    this.stats.begin();

    const time = Date.now() / 4000;

    this.sphereMesh.position.x = this.orbitRadius * Math.cos(time);
    this.sphereMesh.position.z = this.orbitRadius * Math.sin(time) - 100;

    if (this.postprocessing.enabled) {
      this.clipPosition.x = this.sunPosition.x;
      this.clipPosition.y = this.sunPosition.y;
      this.clipPosition.z = this.sunPosition.z;
      this.clipPosition.w = 1;

      this.clipPosition.applyMatrix4(this.camera.matrixWorldInverse).applyMatrix4(this.camera.projectionMatrix);

      // Perspective divide (produce NDC space)
      this.clipPosition.x /= this.clipPosition.w;
      this.clipPosition.y /= this.clipPosition.w;

      this.screenSpacePosition.x = (this.clipPosition.x + 1) / 2;
      this.screenSpacePosition.y = (this.clipPosition.y + 1) / 2;
      this.screenSpacePosition.z = this.clipPosition.z;

      this.postprocessing.godrayGenUniforms['vSunPositionScreenSpace'].value.copy(this.screenSpacePosition);
      this.postprocessing.godraysFakeSunUniforms['vSunPositionScreenSpace'].value.copy(this.screenSpacePosition);

      // Draw sky and sun
      this.renderer.setRenderTarget(this.postprocessing.rtTextureColors);
      this.renderer.clear(true, true, false);

      const sunsqH = 0.74 * this.container.clientHeight;
      const sunsqW = 0.74 * this.container.clientHeight;

      this.screenSpacePosition.x *= this.container.clientWidth;
      this.screenSpacePosition.y *= this.container.clientHeight;

      this.renderer.setScissor(
        this.screenSpacePosition.x - sunsqW / 2,
        this.screenSpacePosition.y - sunsqH / 2,
        sunsqW,
        sunsqH
      );
      this.renderer.setScissorTest(true);

      this.postprocessing.godraysFakeSunUniforms['fAspect'].value = this.container.clientWidth / this.container.clientHeight;

      this.postprocessing.scene.overrideMaterial = this.postprocessing.materialGodraysFakeSun;
      this.renderer.setRenderTarget(this.postprocessing.rtTextureColors);
      this.renderer.render(this.postprocessing.scene, this.postprocessing.camera);

      this.renderer.setScissorTest(false);

      // Draw scene objects
      this.scene.overrideMaterial = null;
      this.renderer.setRenderTarget(this.postprocessing.rtTextureColors);
      this.renderer.render(this.scene, this.camera);

      // Depth
      this.scene.overrideMaterial = this.materialDepth;
      this.renderer.setRenderTarget(this.postprocessing.rtTextureDepth);
      this.renderer.clear();
      this.renderer.render(this.scene, this.camera);

      this.postprocessing.godrayMaskUniforms['tInput'].value = this.postprocessing.rtTextureDepth.texture;

      this.postprocessing.scene.overrideMaterial = this.postprocessing.materialGodraysDepthMask;
      this.renderer.setRenderTarget(this.postprocessing.rtTextureDepthMask);
      this.renderer.render(this.postprocessing.scene, this.postprocessing.camera);

      // Render god-rays
      const filterLen = 1.0;
      const TAPS_PER_PASS = 6.0;

      // Pass 1 - render into first ping-pong target
      this.filterGodRays(
        this.postprocessing.rtTextureDepthMask.texture,
        this.postprocessing.rtTextureGodRays2,
        this.getStepSize(filterLen, TAPS_PER_PASS, 1.0)
      );

      // Pass 2 - render into second ping-pong target
      this.filterGodRays(
        this.postprocessing.rtTextureGodRays2.texture,
        this.postprocessing.rtTextureGodRays1,
        this.getStepSize(filterLen, TAPS_PER_PASS, 2.0)
      );

      // Pass 3 - 1st RT
      this.filterGodRays(
        this.postprocessing.rtTextureGodRays1.texture,
        this.postprocessing.rtTextureGodRays2,
        this.getStepSize(filterLen, TAPS_PER_PASS, 3.0)
      );

      // Final pass - composite god-rays onto colors
      this.postprocessing.godrayCombineUniforms['tColors'].value = this.postprocessing.rtTextureColors.texture;
      this.postprocessing.godrayCombineUniforms['tGodRays'].value = this.postprocessing.rtTextureGodRays2.texture;

      this.postprocessing.scene.overrideMaterial = this.postprocessing.materialGodraysCombine;

      this.renderer.setRenderTarget(null);
      this.renderer.render(this.postprocessing.scene, this.postprocessing.camera);
      this.postprocessing.scene.overrideMaterial = null;
    } else {
      this.renderer.setRenderTarget(null);
      this.renderer.clear();
      this.renderer.render(this.scene, this.camera);
    }

    this.stats.end();
  }

  public dispose() {
    if (this.gui) {
      this.gui.destroy();
    }
    
    this.renderer.dispose();
    this.materialDepth.dispose();
    this.sphereMesh.geometry.dispose();
    (this.sphereMesh.material as THREE.Material).dispose();
    
    Object.values(this.postprocessing).forEach((item: any) => {
      if (item && typeof item.dispose === 'function') {
        item.dispose();
      }
    });
    
    window.removeEventListener('resize', () => this.onWindowResize());
  }
}
